import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../config/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  
  // Run once on component mount to check for existing token
  useEffect(() => {
    console.log('🔐 Auth Provider initialized - checking for existing token');
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          console.log('🔑 Token found in localStorage, fetching user profile');
          await fetchUserProfile(token);
        } catch (err) {
          console.error('🚨 Error validating existing token:', err);
          // Clear invalid token
          logout();
        } finally {
          setLoading(false);
        }
      } else {
        console.log('❌ No token found in localStorage');
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  const sanitizeToken = (token) => {
    if (!token) return null;
    
    // Remove Bearer prefix if present
    let cleanToken = token.startsWith('Bearer ') ? token.substring(7).trim() : token.trim();
    
    // Check if token has valid JWT format (3 parts separated by dots)
    const parts = cleanToken.split('.');
    
    if (parts.length === 3) {
      return cleanToken; // Token is already valid
    }
    
    // Try to extract a valid JWT token
    const jwtPattern = /([A-Za-z0-9_-]+)\.([A-Za-z0-9_-]+)\.([A-Za-z0-9_-]+)/;
    const match = cleanToken.match(jwtPattern);
    
    if (match && match.length === 4) {
      console.log('🛠️ Fixed malformed token format');
      return `${match[1]}.${match[2]}.${match[3]}`;
    }
    
    console.error('❌ Unable to sanitize token:', cleanToken.substring(0, 20) + '...');
    return null; // Token is invalid and can't be fixed
  };
  
  const storeToken = (token) => {
    const cleanToken = sanitizeToken(token);
    
    if (cleanToken) {
      console.log('💾 Storing clean token in localStorage');
      localStorage.setItem('token', cleanToken);
      return cleanToken;
    }
    
    return null;
  };
  
  const getStoredToken = () => {
    const token = localStorage.getItem('token');
    return sanitizeToken(token);
  };
  
  const fetchUserProfile = async (token) => {
    try {
      // Ensure the token is clean before using
      const cleanToken = sanitizeToken(token);
      
      if (!cleanToken) {
        throw new Error('Invalid token format');
      }
      
      // Set auth header with proper Bearer prefix
      axios.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
      
      console.log('🔄 Fetching user profile with token');
      
      // Fetch user data
      const response = await axios.get('/api/users/profile');
      
      console.log('✅ User profile fetched successfully:', response.data);
      setUser(response.data);
      setIsAuthenticated(true);
      setAuthError(null);
      return response.data;
    } catch (err) {
      console.error('❌ Error fetching user profile:', err.response?.status, err.message);
      
      // Clear invalid token
      logout();
      
      throw err;
    }
  };
  
  const login = async (email, password) => {
    try {
      setLoading(true);
      setAuthError(null);
      
      console.log('🔄 Attempting login with email:', email);
      
      // Send login request
      const response = await axios.post('/api/auth/login', { 
        email, 
        password 
      });
      
      // Get token from response
      const { token, user: userData } = response.data;
      
      if (!token) {
        throw new Error('No token received from server');
      }
      
      console.log('✅ Login successful, token received');
      
      // Store sanitized token
      const cleanToken = storeToken(token);
      
      if (!cleanToken) {
        throw new Error('Failed to store valid token');
      }
      
      // Set auth header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
      
      // Set user data directly from login response
      setUser(userData);
      setIsAuthenticated(true);
      
      console.log('👤 User authenticated:', userData.username || userData.email);
      
      return true;
    } catch (err) {
      console.error('❌ Login error:', err.response?.status, err.message);
      setAuthError(err.response?.data?.message || 'Login failed');
      setIsAuthenticated(false);
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  const register = async (username, email, password) => {
    try {
      setLoading(true);
      setAuthError(null);
      
      console.log('🔄 Attempting to register user:', username);
      
      const response = await axios.post('/api/auth/register', { 
        username, 
        email, 
        password 
      });
      
      const { token, user: userData, success, message } = response.data;
      
      if (success && token) {
        console.log('✅ Registration successful, token received');
        
        // Store sanitized token
        const cleanToken = storeToken(token);
        
        if (!cleanToken) {
          throw new Error('Failed to store valid token');
        }
        
        // Set auth header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
        
        // Set user data directly from registration response
        setUser(userData);
        setIsAuthenticated(true);
        
        console.log('👤 User registered and authenticated:', userData.username);
        
        return { success: true };
      } else {
        throw new Error(message || 'Registration failed');
      }
    } catch (err) {
      console.error('❌ Registration error:', err.response?.status, err.message);
      const errorMsg = err.response?.data?.message || 'Registration failed';
      setAuthError(errorMsg);
      
      return { 
        success: false, 
        message: errorMsg 
      };
    } finally {
      setLoading(false);
    }
  };
  
  const logout = () => {
    console.log('🚪 Logging out user');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    setAuthError(null);
  };
  
  const updateUser = (userData) => {
    console.log('🔄 Updating user data:', userData?.username || userData?.email || 'unknown user');
    setUser(userData);
  };
  
  // For debugging - log the complete auth state
  console.log('🔐 Auth state:', { 
    isAuthenticated, 
    loading, 
    hasUser: !!user, 
    username: user?.username || 'none',
    role: user?.role || 'none',
    error: authError
  });
  
  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        register,
        logout,
        authError,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
